import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text } from '../../lib/react-native-compat';
import { loadFieldReports, loadFieldReport } from '../../db/company';
import { jobOpenCommitmentsAtom } from '../../store/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import AutoGrowTextarea from './AutoGrowTextarea';

// Labor & Crew editor: rows are crews and subs with a discipline, headcount
// and the hours worked that day; Total MH = headcount × hours. Subs get a
// work-performed notes line under their row.
//
// The sub group opens with a quick-add box: free text, or pick from the job's
// open sub POs (cost types 54100/54200/54300 in jobOpenCommitments). A
// collapsed "Upcoming subs" panel lists sub POs whose start date is after the
// report date, from the PO lines' rental start/end dates.

// Common field disciplines for the datalist — free typing is still allowed.
const DISCIPLINES = [
    'Civil',
    'Sitework / Dirtwork',
    'Mechanical',
    'Piping',
    'Pipeline',
    'Structural',
    'Electrical',
    'I&E',
    'Fabrication',
    'Coatings / Paint',
    'Testing / Commissioning',
    'Demo',
    'Project Support',
];

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};
const round1 = (v) => Math.round(v * 10) / 10;

// JDE cost types that mark a commitment line as subcontract work — same set
// the Subcontractors tab pages on.
const SUB_COST_TYPES = new Set(['54100', '54200', '54300']);

const norm = (v) => String(v ?? '').trim().toLowerCase();
const lineAmt = (r) => {
    for (const k of ['Original_Line_Amount', 'PO_Ordered_Amount', 'PO_Open_Amount']) {
        const n = parseFloat(r?.[k]);
        if (Number.isFinite(n) && n !== 0) return Math.abs(n);
    }
    return 0;
};

const CELL_BORDER = '1px solid #CBD5E1';

const thStyle = {
    border: CELL_BORDER,
    background: '#F1F5F9',
    padding: '6px 8px',
    fontSize: 11,
    fontWeight: 700,
    color: '#334155',
    textAlign: 'left',
    verticalAlign: 'bottom',
};

const tdStyle = { border: CELL_BORDER, padding: 0, verticalAlign: 'middle', background: '#fff' };

const cellInput = {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 13,
    color: '#0F172A',
    padding: '7px 8px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
};

const groupHeaderTd = {
    border: CELL_BORDER,
    background: '#E2E8F0',
    padding: '5px 8px',
    fontSize: 11,
    fontWeight: 700,
    color: '#1E293B',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
};

// '2026-07-18' → '7/18'; full = 'Fri 7/18/2026'
const shortDate = (iso) => (iso ? `${parseInt(iso.slice(5, 7), 10)}/${parseInt(iso.slice(8, 10), 10)}` : '');
// PO date window → '7/25 – 8/8'; same-day windows collapse to one date.
const dateRange = (start, end) => {
    if (start && end && start !== end) return `${shortDate(start)} – ${shortDate(end)}`;
    return shortDate(start || end);
};
const fullDate = (iso) => {
    if (!iso) return '';
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', year: 'numeric' });
};

const LaborMatrix = ({ readOnly }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [openCommitments] = useAtom(jobOpenCommitmentsAtom);
    // Most recent *submitted* report before this one: its per-crew headcount
    // and hours render as read-only comparison columns.
    const [prev, setPrev] = useState(null); // { date, byKey: { 'group|name': { headcount, hours } } }
    // Sub quick-add box + collapsed upcoming-subs panel state.
    const [subQuery, setSubQuery] = useState('');
    const [subPickerOpen, setSubPickerOpen] = useState(false);
    const [showUpcoming, setShowUpcoming] = useState(false);
    const subBoxRef = useRef(null);

    useEffect(() => {
        const onDown = (e) => {
            if (subBoxRef.current && !subBoxRef.current.contains(e.target)) setSubPickerOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);

    // One entry per open sub PO: owning vendor, the largest-dollar line's
    // description, and the PO's date window (min start / max end across lines).
    const subPOs = useMemo(() => {
        const byPO = {};
        (openCommitments || []).forEach((r) => {
            if (!SUB_COST_TYPES.has(String(r?.Cost_Type_Code ?? '').trim())) return;
            const po = String(r?.PO_Number ?? '').trim();
            const vendor = String(r?.Supplier_AB_Name ?? '').trim();
            if (!po && !vendor) return;
            const key = `${vendor}|${po}`;
            if (!byPO[key]) byPO[key] = { vendor, po, desc: '', descAmt: -1, start: '', end: '' };
            const info = byPO[key];
            const a = lineAmt(r);
            if (a > info.descAmt) {
                info.descAmt = a;
                info.desc = [r?.PO_Line_Description, r?.PO_Line_Description_2]
                    .map((v) => String(v ?? '').trim()).filter(Boolean).join(' ');
            }
            const st = r?.Rental_Start_Date ? String(r.Rental_Start_Date).slice(0, 10) : '';
            const en = r?.Rental_End_Date ? String(r.Rental_End_Date).slice(0, 10) : '';
            if (st && (!info.start || st < info.start)) info.start = st;
            if (en && (!info.end || en > info.end)) info.end = en;
        });
        return Object.values(byPO).sort((a, b) => a.vendor.localeCompare(b.vendor) || a.po.localeCompare(b.po));
    }, [openCommitments]);

    useEffect(() => {
        let alive = true;
        (async () => {
            if (!report?.buId) return;
            const list = await loadFieldReports({ buId: report.buId });
            const prior = (list?.reports || [])
                .filter((r) => r.Status === 'Submitted' && String(r.ReportDate).slice(0, 10) < (report.reportDate || ''))
                .sort((a, b) => String(b.ReportDate).localeCompare(String(a.ReportDate)))[0];
            if (!prior) { if (alive) setPrev({ date: null, byKey: {} }); return; }
            const res = await loadFieldReport({ reportId: prior.ReportID });
            const byKey = {};
            (res?.labor || []).forEach((r) => {
                const group = String(r.Classification) === 'Sub' ? 'sub' : 'crew';
                const name = String(r.CrewForeman || r.Company || '');
                const key = `${group}|${name}`;
                if (!byKey[key]) byKey[key] = { headcount: parseFloat(r.Headcount) || 0, hours: 0 };
                const hrs = parseFloat(r.STHours);
                if (Number.isFinite(hrs)) byKey[key].hours = Math.round((byKey[key].hours + hrs) * 10) / 10;
            });
            if (alive) setPrev({ date: String(prior.ReportDate).slice(0, 10), byKey });
        })();
        return () => { alive = false; };
    }, [report?.buId, report?.reportDate]);

    if (!report) return null;
    const rows = report.sections?.labor || [];
    const prevOf = (r) => prev?.byKey?.[`${r.group === 'sub' ? 'sub' : 'crew'}|${r.name || ''}`];

    const setRows = (next) => {
        setReport({ ...report, sections: { ...report.sections, labor: next } });
        setDirty(true);
    };

    const updateRow = (i, patch) => {
        const next = rows.slice();
        next[i] = { ...next[i], ...patch };
        setRows(next);
    };
    const addRow = (group) => setRows([...rows, { group, name: '', headcount: '', hoursWorked: '' }]);
    const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

    // Quick-add a sub by name (free text or a picked PO). Fills the seeded
    // blank sub row first so picking doesn't leave an empty row behind.
    const addSub = (name, poNumber, poStart, poEnd) => {
        const label = String(name || '').trim();
        if (!label) return;
        const patch = { name: label, poNumber: poNumber || '', poStart: poStart || '', poEnd: poEnd || '' };
        const blankIdx = rows.findIndex((r) => r.group === 'sub'
            && !String(r.name || '').trim() && !String(r.headcount || '').trim()
            && !String(r.hoursWorked || '').trim() && !String(r.notes || '').trim());
        if (blankIdx >= 0) updateRow(blankIdx, patch);
        else setRows([...rows, { group: 'sub', ...patch, headcount: '', hoursWorked: '' }]);
        setSubQuery('');
        setSubPickerOpen(false);
    };

    // Start – stop shown under the sub's name: the dates stored when the PO
    // was picked, else looked up from the open sub POs by PO number (covers
    // rows saved before the dates were stored).
    const poDatesOf = (r) => {
        const own = dateRange(r.poStart, r.poEnd);
        if (own) return own;
        const po = String(r.poNumber || '').trim();
        const m = po ? subPOs.find((p) => p.po === po) : null;
        return m ? dateRange(m.start, m.end) : '';
    };

    const q = norm(subQuery);
    const filteredSubPOs = subPOs.filter((p) => !q
        || norm(p.vendor).includes(q) || norm(p.po).includes(q) || norm(p.desc).includes(q));

    // Sub POs that haven't started yet as of the report date.
    const upcomingSubs = subPOs
        .filter((p) => p.start && p.start > (report?.reportDate || ''))
        .sort((a, b) => a.start.localeCompare(b.start));

    const rowMH = (r) => round1(num(r.headcount) * num(r.hoursWorked));

    const groups = [
        { key: 'crew', label: 'Strike Crews', noun: 'crew', namePh: 'Crew A — T. Boone' },
        { key: 'sub', label: 'Subcontractors On Site', noun: 'sub', namePh: 'NDT Partners' },
    ];

    const colCount = 7 + (readOnly ? 0 : 1);
    const lastLabel = prev?.date ? ` (${shortDate(prev.date)})` : '';

    const prevCell = (val) => (
        <td style={{ ...tdStyle, background: '#F8FAFC' }}>
            <div style={{ ...cellInput, color: '#94A3B8', textAlign: 'right' }}>
                {val == null || val === 0 ? '—' : val}
            </div>
        </td>
    );

    const renderRows = (group) => rows
        .map((r, i) => ({ r, i }))
        .filter(({ r }) => (group === 'sub' ? r.group === 'sub' : r.group !== 'sub'))
        .map(({ r, i }) => (
            <React.Fragment key={i}>
            <tr>
                <td style={tdStyle}>
                    <input
                        type="text"
                        value={r.name || ''}
                        disabled={readOnly}
                        placeholder={group === 'sub' ? 'Subcontractor' : 'Crew / Foreman'}
                        onChange={(e) => updateRow(i, { name: e.target.value })}
                        style={cellInput}
                    />
                    {group === 'sub' && (r.poNumber || poDatesOf(r)) ? (
                        <div style={{ fontSize: 10, color: '#64748B', padding: '0 8px 5px' }}>
                            {[r.poNumber ? `PO ${r.poNumber}` : '', poDatesOf(r)].filter(Boolean).join('  ·  ')}
                        </div>
                    ) : null}
                </td>
                <td style={tdStyle}>
                    <input
                        type="text"
                        list="fieldReportDisciplines"
                        value={r.discipline || ''}
                        disabled={readOnly}
                        placeholder="Civil / Mechanical…"
                        onChange={(e) => updateRow(i, { discipline: e.target.value })}
                        style={cellInput}
                    />
                </td>
                <td style={tdStyle}>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={r.headcount ?? ''}
                        disabled={readOnly}
                        onChange={(e) => updateRow(i, { headcount: e.target.value })}
                        style={{ ...cellInput, textAlign: 'right' }}
                    />
                </td>
                {prevCell(prevOf(r)?.headcount)}
                <td style={tdStyle}>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={r.hoursWorked ?? ''}
                        disabled={readOnly}
                        onChange={(e) => updateRow(i, { hoursWorked: e.target.value })}
                        style={{ ...cellInput, textAlign: 'right' }}
                    />
                </td>
                {prevCell(prevOf(r)?.hours)}
                <td style={{ ...tdStyle, background: '#F8FAFC' }}>
                    <div style={{ ...cellInput, fontWeight: 600, color: '#475569', textAlign: 'right' }}>
                        {rowMH(r) || '—'}
                    </div>
                </td>
                {!readOnly && (
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <button
                            onClick={() => delRow(i)}
                            title="Remove row"
                            style={{ border: 'none', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '6px 8px' }}
                        >×</button>
                    </td>
                )}
            </tr>
            {/* Subs get a work-performed notes line + % complete under their row. */}
            {group === 'sub' && (!readOnly || r.notes || (r.percentComplete ?? '') !== '' || r.onSchedule) && (
                <tr>
                    <td colSpan={colCount} style={{ ...tdStyle, background: '#FBFDFF' }}>
                        {/* Sticky + viewport-capped so the whole strip stays visible even
                            when the table itself scrolls sideways. 1/3 rule: notes get
                            ~2/3 of the strip, the % / schedule cluster ~1/3; the cluster
                            wraps under the notes on phones. Notes auto-grow as typed. */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', position: 'sticky', left: 0, maxWidth: 'calc(100vw - 90px)' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', flex: '2 1 240px', minWidth: 0 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, padding: '9px 0 0 8px', whiteSpace: 'nowrap' }}>
                                    Work performed
                                </span>
                                <AutoGrowTextarea
                                    value={r.notes || ''}
                                    disabled={readOnly}
                                    placeholder="What did this sub work on today?"
                                    onChange={(e) => updateRow(i, { notes: e.target.value })}
                                    style={cellInput}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', flex: '1 0 auto', justifyContent: 'flex-end', paddingTop: 2, paddingBottom: 2, paddingRight: 8 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, paddingLeft: 8, whiteSpace: 'nowrap' }}>
                                    % Complete
                                </span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={r.percentComplete ?? ''}
                                    disabled={readOnly}
                                    onChange={(e) => updateRow(i, { percentComplete: e.target.value })}
                                    placeholder="%"
                                    style={{ ...cellInput, width: 52, flexShrink: 0, textAlign: 'right', padding: '4px 6px', border: '1px solid #CBD5E1', borderRadius: 6, background: '#fff', marginLeft: 6 }}
                                />
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, paddingLeft: 10, whiteSpace: 'nowrap' }}>
                                    On schedule?
                                </span>
                                {['Y', 'N'].map((v) => (
                                    <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 3, paddingLeft: 6, fontSize: 12, color: '#0F172A', whiteSpace: 'nowrap', cursor: readOnly ? 'default' : 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={r.onSchedule === v}
                                            disabled={readOnly}
                                            onChange={(e) => updateRow(i, { onSchedule: e.target.checked ? v : '' })}
                                            style={{ width: 14, height: 14, accentColor: '#1D4ED8', cursor: 'inherit' }}
                                        />
                                        {v === 'Y' ? 'Yes' : 'No'}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </td>
                </tr>
            )}
            </React.Fragment>
        ));

    const groupHeadcount = (group) => rows
        .filter((r) => (group === 'sub' ? r.group === 'sub' : r.group !== 'sub'))
        .reduce((t, r) => t + num(r.headcount), 0);

    const totalHours = round1(rows.reduce((t, r) => t + num(r.hoursWorked), 0));
    const totalMH = round1(rows.reduce((t, r) => t + rowMH(r), 0));

    return (
        <View>
            <datalist id="fieldReportDisciplines">
                {DISCIPLINES.map((d) => <option key={d} value={d} />)}
            </datalist>
            {/* Today vs. last submitted report */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 16, rowGap: 2, marginBottom: 6 }}>
                <Text style={{ fontSize: 12, color: '#475569' }}>
                    <Text style={{ fontWeight: '700' }}>Today: </Text>{fullDate(report.reportDate)}
                </Text>
                <Text style={{ fontSize: 12, color: '#475569' }}>
                    <Text style={{ fontWeight: '700' }}>Last submission: </Text>
                    {prev == null ? '…' : prev.date ? fullDate(prev.date) : 'none yet'}
                </Text>
            </View>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: 220, minWidth: 160 }}>Crew / Sub</th>
                            <th style={{ ...thStyle, width: 140, minWidth: 110 }}>Discipline</th>
                            <th style={{ ...thStyle, width: 70 }}>Headcount</th>
                            <th style={{ ...thStyle, width: 70, color: '#94A3B8' }}>{`Last HC${lastLabel}`}</th>
                            <th style={{ ...thStyle, width: 70 }}>Hours</th>
                            <th style={{ ...thStyle, width: 70, color: '#94A3B8' }}>{`Last Hrs${lastLabel}`}</th>
                            <th style={{ ...thStyle, width: 75 }}>Total MH</th>
                            {!readOnly && <th style={{ ...thStyle, minWidth: 30, width: 30 }} />}
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((g) => (
                            <React.Fragment key={g.key}>
                                <tr>
                                    <td colSpan={colCount} style={groupHeaderTd}>
                                        {`${g.label} — headcount ${groupHeadcount(g.key)}`}
                                    </td>
                                </tr>
                                {g.key === 'sub' && (!readOnly || upcomingSubs.length > 0) && (
                                    <tr>
                                        <td colSpan={colCount} style={{ ...tdStyle, background: '#FBFDFF' }}>
                                            {/* Sticky-left like the notes strip so the box stays
                                                visible when the table scrolls sideways. The PO
                                                suggestion list renders inline (not overlaid) so the
                                                horizontal-scroll container never clips it. */}
                                            <div ref={subBoxRef} style={{ position: 'sticky', left: 0, maxWidth: 'calc(100vw - 90px)', padding: '6px 8px', boxSizing: 'border-box' }}>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: 4 }}>
                                                    {!readOnly && (
                                                        <input
                                                            type="text"
                                                            value={subQuery}
                                                            placeholder={subPOs.length > 0 ? 'Add a sub — type a name or pick from the job’s sub POs…' : 'Add a sub — type a name…'}
                                                            onFocus={() => setSubPickerOpen(true)}
                                                            onChange={(e) => { setSubQuery(e.target.value); setSubPickerOpen(true); }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') addSub(subQuery, '');
                                                                if (e.key === 'Escape') setSubPickerOpen(false);
                                                            }}
                                                            style={{ flex: '2 1 240px', minWidth: 0, border: '1px solid #CBD5E1', borderRadius: 8, background: '#fff', fontSize: 13, color: '#0F172A', padding: '7px 10px', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }}
                                                        />
                                                    )}
                                                    {upcomingSubs.length > 0 && (
                                                        <button
                                                            onClick={() => setShowUpcoming((v) => !v)}
                                                            style={{ border: 'none', background: 'transparent', color: '#1D4ED8', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '6px 8px', whiteSpace: 'nowrap' }}
                                                        >{`${showUpcoming ? '▾' : '▸'} Upcoming subs (${upcomingSubs.length})`}</button>
                                                    )}
                                                </div>
                                                {!readOnly && subPickerOpen && filteredSubPOs.length > 0 && (
                                                    <div style={{ border: '1px solid #CBD5E1', borderRadius: 8, background: '#fff', marginTop: 4, maxHeight: 190, overflowY: 'auto' }}>
                                                        {filteredSubPOs.map((p) => (
                                                            <div
                                                                key={`${p.vendor}|${p.po}`}
                                                                onMouseDown={(e) => { e.preventDefault(); addSub(p.vendor, p.po, p.start, p.end); }}
                                                                style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9' }}
                                                            >
                                                                <div style={{ fontSize: 13, color: '#0F172A', fontWeight: 600 }}>
                                                                    {p.vendor || '(no vendor)'}
                                                                    <span style={{ fontWeight: 400, color: '#64748B' }}>
                                                                        {`${p.po ? `  ·  PO ${p.po}` : ''}${dateRange(p.start, p.end) ? `  ·  ${dateRange(p.start, p.end)}` : ''}`}
                                                                    </span>
                                                                </div>
                                                                {p.desc ? (
                                                                    <div style={{ fontSize: 11, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.desc}</div>
                                                                ) : null}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {showUpcoming && upcomingSubs.length > 0 && (
                                                    <div style={{ border: '1px solid #E2E8F0', borderRadius: 8, background: '#F8FAFC', marginTop: 4, padding: '4px 10px' }}>
                                                        {upcomingSubs.map((p) => (
                                                            <div key={`${p.vendor}|${p.po}`} style={{ display: 'flex', flexWrap: 'wrap', columnGap: 10, alignItems: 'baseline', padding: '3px 0', borderBottom: '1px solid #EEF2F6' }}>
                                                                <span style={{ fontSize: 12, fontWeight: 600, color: '#0F172A' }}>{p.vendor || '(no vendor)'}</span>
                                                                {p.po ? <span style={{ fontSize: 11, color: '#64748B' }}>{`PO ${p.po}`}</span> : null}
                                                                <span style={{ fontSize: 11, color: '#334155' }}>
                                                                    {`starts ${shortDate(p.start)}${p.end ? ` – ends ${shortDate(p.end)}` : ''}`}
                                                                </span>
                                                                {p.desc ? <span style={{ fontSize: 11, color: '#64748B', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 140px' }}>{p.desc}</span> : null}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {renderRows(g.key)}
                                {!readOnly && (
                                    <tr>
                                        <td colSpan={colCount} style={{ ...tdStyle, background: '#F8FAFC' }}>
                                            <button
                                                onClick={() => addRow(g.key)}
                                                style={{ border: 'none', background: 'transparent', color: '#1D4ED8', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '6px 8px' }}
                                            >{`+ Add ${g.noun}`}</button>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                        {rows.length > 0 && (
                            <tr>
                                <td style={{ ...tdStyle, background: '#F1F5F9' }}>
                                    <div style={{ ...cellInput, fontWeight: 700 }}>Totals</div>
                                </td>
                                <td style={{ ...tdStyle, background: '#F1F5F9' }} />
                                <td style={{ ...tdStyle, background: '#F1F5F9' }}>
                                    <div style={{ ...cellInput, fontWeight: 700, textAlign: 'right' }}>{groupHeadcount('crew') + groupHeadcount('sub')}</div>
                                </td>
                                <td style={{ ...tdStyle, background: '#F1F5F9' }} />
                                <td style={{ ...tdStyle, background: '#F1F5F9' }}>
                                    <div style={{ ...cellInput, fontWeight: 700, textAlign: 'right' }}>{totalHours || ''}</div>
                                </td>
                                <td style={{ ...tdStyle, background: '#F1F5F9' }} />
                                <td style={{ ...tdStyle, background: '#F1F5F9' }}>
                                    <div style={{ ...cellInput, fontWeight: 700, textAlign: 'right' }}>{totalMH || ''}</div>
                                </td>
                                {!readOnly && <td style={{ ...tdStyle, background: '#F1F5F9' }} />}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {!readOnly && (
                <Text style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                    MH = headcount × hours
                </Text>
            )}
        </View>
    );
};

export default LaborMatrix;
